import axios from "axios";
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Joyride, {
  ACTIONS,
  CallBackProps,
  EVENTS,
  STATUS,
  Step,
} from "react-joyride";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAgencyAuth } from "../context/AgencyAuthContext";
import { useTheme } from "../context/ThemeContext";
import { useSurveyGate } from "../components/PostTrialSurvey/SurveyGateContext";
import { computeEligibleTutorials, EligibilityUser } from "./eligibility";
import {
  dialogHostFor,
  isVisible,
  matchesRoute,
  relocateTourPortals,
  restoreTourPortals,
  stripTourParam,
  TOUR_QUERY_PARAM,
  waitForVisible,
  Waiter,
} from "./helpers";
import { ALL_TUTORIALS, getTutorialById } from "./index";
import TutorialTooltip from "./TutorialTooltip";
import { TourStep, TutorialAudience, TutorialDefinition } from "./types";

export type TutorialStartSource = "academy";

export interface StartTutorialOptions {
  /** Where the start came from; the Academy lesson page passes "academy". */
  from?: TutorialStartSource;
}

interface TutorialContextValue {
  pendingTutorials: TutorialDefinition[];
  allTutorials: TutorialDefinition[];
  seen: string[];
  isRunning: boolean;
  /** Id of the tour on screen, or null. Its `lessonId` is on the definition. */
  runningTourId: string | null;
  /** Who started the running tour (undefined for auto-starts). */
  runningFrom: TutorialStartSource | undefined;
  startTutorial: (id: string, options?: StartTutorialOptions) => void;
  replayTutorial: (id: string, options?: StartTutorialOptions) => Promise<void>;
  dismissCurrent: () => void;
}

export const TutorialContext = createContext<TutorialContextValue | null>(
  null,
);

const AUTO_START_DELAY_MS = 800;
const TARGET_POLL_INTERVAL_MS = 200;
/** Auto-start: the page just rendered, the target is either there or not. */
const AUTO_START_POLL_TIMEOUT_MS = 3000;
/** Manual start: may follow a `navigate(startRoute)`, so allow a full load. */
const MANUAL_START_POLL_TIMEOUT_MS = 6000;
/** Default budget for a step's `waitFor`. */
const STEP_WAIT_TIMEOUT_MS = 6000;

interface CoreProps extends React.PropsWithChildren {
  /** Who is signed in as far as tours care; null until known. Memoize it. */
  subject: EligibilityUser | null;
  audience: TutorialAudience;
  /** POST {tutorialId} marks seen; DELETE `${endpoint}/:id` resets. */
  seenEndpoint: string;
}

/** Officer app: tours for officers, state in users.seen_tutorials. */
export const TutorialProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { user } = useAuth();
  const subject = useMemo<EligibilityUser | null>(
    () =>
      user
        ? {
            createdAt: (user as any).created_at,
            seen: Array.isArray((user as any).seen_tutorials)
              ? ((user as any).seen_tutorials as string[])
              : [],
          }
        : null,
    [user],
  );
  return (
    <TutorialProviderCore
      subject={subject}
      audience="officer"
      seenEndpoint="/api/settings/seen-tutorials"
    >
      {children}
    </TutorialProviderCore>
  );
};

/** Agency portal: tours for agency admins, state in agency_admins.seen_tutorials. */
export const AgencyTutorialProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { agency } = useAgencyAuth();
  const subject = useMemo<EligibilityUser | null>(
    () =>
      agency && agency.type === "agency_admin"
        ? {
            createdAt: agency.admin_created_at,
            seen: agency.admin_seen_tutorials ?? [],
          }
        : null,
    [agency],
  );
  return (
    <TutorialProviderCore
      subject={subject}
      audience="agency"
      seenEndpoint="/api/agency/seen-tutorials"
    >
      {children}
    </TutorialProviderCore>
  );
};

const TutorialProviderCore: React.FC<CoreProps> = ({
  children,
  subject: user,
  audience,
  seenEndpoint,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  // The post-trial survey wins over a tour: hold the auto-start until its
  // eligibility is known and it isn't on screen.
  const { surveyOpen, surveyResolved } = useSurveyGate();

  const [seen, setSeen] = useState<string[]>([]);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [runningFrom, setRunningFrom] = useState<
    TutorialStartSource | undefined
  >(undefined);
  const [stepIndex, setStepIndex] = useState(0);
  const autoStartTimer = useRef<number | null>(null);
  // One auto-start per page load. Flips true the moment a tour actually starts
  // (NOT when the silent-abort branch fires) so a single login can never
  // bombard the user with multiple back-to-back tours. Resets on hard refresh.
  const hasAutoStartedThisSessionRef = useRef(false);
  // A step change held back by `waitFor` (or a first-target poll). Only one
  // can be in flight; starting another cancels it.
  const pendingRef = useRef<Waiter | null>(null);
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  const cancelPending = useCallback(() => {
    pendingRef.current?.cancel();
    pendingRef.current = null;
  }, []);

  // Hydrate seen state from the user object — already fetched by AuthContext.
  useEffect(() => {
    if (user) setSeen(user.seen);
  }, [user]);

  const pendingTutorials = useMemo(() => {
    if (!user) return [];
    return computeEligibleTutorials(
      ALL_TUTORIALS,
      { createdAt: user.createdAt, seen },
      audience,
    );
  }, [user, seen, audience]);

  const beginTour = useCallback(
    (tour: TutorialDefinition, from: TutorialStartSource | undefined) => {
      hasAutoStartedThisSessionRef.current = true;
      tour.steps[0]?.onEnter?.();
      setStepIndex(0);
      setRunningFrom(from);
      setRunningId(tour.id);
    },
    [],
  );

  const waitForTargetsThenStart = useCallback(
    (
      tour: TutorialDefinition,
      timeoutMs: number,
      from?: TutorialStartSource,
    ) => {
      cancelPending();
      const firstTarget = tour.steps[0]?.target;
      if (!firstTarget || typeof firstTarget !== "string") {
        beginTour(tour, from);
        return;
      }
      const deadline = Date.now() + timeoutMs;
      let cancelled = false;
      let timer: number | null = null;
      const tick = () => {
        if (cancelled) return;
        if (document.querySelector(firstTarget)) {
          pendingRef.current = null;
          beginTour(tour, from);
        } else if (Date.now() < deadline) {
          timer = window.setTimeout(tick, TARGET_POLL_INTERVAL_MS);
        } else {
          pendingRef.current = null;
        }
        // Otherwise: silently abort. The tour will surface again on the next
        // matching navigation. We deliberately do NOT set the session flag in
        // this branch — a tour whose target never appeared shouldn't burn the
        // session quota.
      };
      pendingRef.current = {
        promise: Promise.resolve(null),
        cancel: () => {
          cancelled = true;
          if (timer !== null) window.clearTimeout(timer);
        },
      };
      tick();
    },
    [beginTour, cancelPending],
  );

  // Auto-start the next eligible tutorial whenever the route changes.
  // Capped at one auto-start per page load — see hasAutoStartedThisSessionRef.
  useEffect(() => {
    if (!user || runningId) return;
    if (hasAutoStartedThisSessionRef.current) return;
    if (surveyOpen || !surveyResolved) return;

    const match = pendingTutorials.find((t) => {
      if (t.autoStart === false) return false;
      return matchesRoute(t.triggerRoute, location.pathname);
    });

    if (!match) return;

    if (autoStartTimer.current) {
      window.clearTimeout(autoStartTimer.current);
    }
    autoStartTimer.current = window.setTimeout(
      () => waitForTargetsThenStart(match, AUTO_START_POLL_TIMEOUT_MS),
      AUTO_START_DELAY_MS,
    );

    return () => {
      if (autoStartTimer.current) {
        window.clearTimeout(autoStartTimer.current);
        autoStartTimer.current = null;
      }
    };
  }, [
    location.pathname,
    pendingTutorials,
    runningId,
    user,
    surveyOpen,
    surveyResolved,
    waitForTargetsThenStart,
  ]);

  const markSeen = useCallback(async (id: string) => {
    setSeen((prev) => (prev.includes(id) ? prev : [...prev, id]));
    try {
      await axios.post(seenEndpoint, { tutorialId: id });
    } catch (err) {
      console.error("Failed to persist seen tutorial:", err);
    }
  }, [seenEndpoint]);

  const runningTour = useMemo(
    () => (runningId ? getTutorialById(runningId) ?? null : null),
    [runningId],
  );

  const endTour = useCallback(
    (persist: boolean) => {
      cancelPending();
      if (persist && runningId) markSeen(runningId);
      setRunningId(null);
      setRunningFrom(undefined);
      setStepIndex(0);
    },
    [cancelPending, markSeen, runningId],
  );

  /**
   * Move to `index`, honouring the step's `waitFor`: the stepIndex change is
   * held until the selector is present and visible, then `onEnter` runs and
   * the step is committed. On timeout it is committed anyway so joyride's
   * TARGET_NOT_FOUND skip takes over.
   */
  const goToStep = useCallback(
    (tour: TutorialDefinition, index: number) => {
      cancelPending();
      const step = tour.steps[index] as TourStep | undefined;
      const commit = () => {
        pendingRef.current = null;
        step?.onEnter?.();
        setStepIndex(index);
      };
      if (!step?.waitFor || isVisible(document.querySelector(step.waitFor))) {
        commit();
        return;
      }
      const waiter = waitForVisible(
        step.waitFor,
        step.waitTimeoutMs ?? STEP_WAIT_TIMEOUT_MS,
        TARGET_POLL_INTERVAL_MS,
      );
      pendingRef.current = waiter;
      waiter.promise.then(() => {
        // A newer transition (or the tour ending) replaced us: stay quiet.
        if (pendingRef.current !== waiter) return;
        commit();
      });
    },
    [cancelPending],
  );

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { status, index, type, action } = data;

      if (
        status === STATUS.FINISHED ||
        status === STATUS.SKIPPED ||
        action === ACTIONS.CLOSE
      ) {
        endTour(true);
        return;
      }

      if (
        type === EVENTS.STEP_AFTER ||
        type === EVENTS.TARGET_NOT_FOUND
      ) {
        const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
        if (runningTour && nextIndex >= 0 && nextIndex < runningTour.steps.length) {
          goToStep(runningTour, nextIndex);
        } else {
          // Past either end: let joyride finish the tour as it always has.
          setStepIndex(nextIndex);
        }
      }
    },
    [endTour, goToStep, runningTour],
  );

  // `advanceOn`: one capture-phase listener on document for the current step.
  useEffect(() => {
    if (!runningTour) return;
    const step = runningTour.steps[stepIndex];
    const advanceOn = step?.advanceOn;
    if (!advanceOn) return;
    const handler = (event: Event) => {
      const target = event.target as Element | null;
      if (!target || typeof target.closest !== "function") return;
      if (!target.closest(advanceOn.selector)) return;
      document.removeEventListener(advanceOn.event, handler, true);
      goToStep(runningTour, stepIndex + 1);
    };
    document.addEventListener(advanceOn.event, handler, true);
    return () => document.removeEventListener(advanceOn.event, handler, true);
  }, [runningTour, stepIndex, goToStep]);

  // Targets inside an open `<dialog>` (top layer) need the joyride portals
  // moved into that dialog, or the tooltip renders underneath the modal.
  useEffect(() => {
    if (!runningTour) return;
    const step = runningTour.steps[stepIndex];
    const selector = typeof step?.target === "string" ? step.target : null;
    const el = selector ? document.querySelector(selector) : null;
    const dialog = dialogHostFor(el);
    const host = dialog ?? document.body;
    const sync = () => relocateTourPortals(host, stepIndex);
    sync();
    // react-floater appends a fresh node to <body> when the step mounts.
    const observer = new MutationObserver(sync);
    observer.observe(document.body, { childList: true });
    if (!dialog) return () => observer.disconnect();

    // joyride scrolls the document, not the modal box.
    el?.scrollIntoView({ block: "nearest", inline: "nearest" });
    // The officer closed the modal mid-step (X / Escape): the tooltip would
    // vanish with it, so stop the tour rather than strand it. A transition
    // already in flight (the step's own action closed the modal) is left to
    // finish.
    const onClose = () => {
      if (pendingRef.current) return;
      restoreTourPortals();
      endTour(false);
    };
    dialog.addEventListener("close", onClose);
    return () => {
      observer.disconnect();
      dialog.removeEventListener("close", onClose);
    };
  }, [runningTour, stepIndex, endTour]);

  // When the tour is over, hand the portal nodes back to <body> so joyride's
  // own unmount cleanup finds them (or drop them if already emptied).
  useEffect(() => {
    if (runningId) return;
    restoreTourPortals();
  }, [runningId]);

  const startTutorial = useCallback(
    (id: string, options?: StartTutorialOptions) => {
      const t = getTutorialById(id);
      if (!t) return;
      if (t.startRoute && !matchesRoute(t.triggerRoute, pathnameRef.current)) {
        navigate(t.startRoute);
      }
      waitForTargetsThenStart(t, MANUAL_START_POLL_TIMEOUT_MS, options?.from);
    },
    [navigate, waitForTargetsThenStart],
  );

  // `?tour=<id>` on load: the Academy opened this tab to run a walkthrough.
  // Start it once the user is known (the tour's targets need the app shell),
  // then drop the parameter so a reload or a copied link does not replay it.
  const urlStartDoneRef = useRef(false);
  useEffect(() => {
    if (!user || urlStartDoneRef.current) return;
    const id = new URLSearchParams(location.search).get(TOUR_QUERY_PARAM);
    if (!id) return;
    urlStartDoneRef.current = true;
    navigate(
      { pathname: location.pathname, search: stripTourParam(location.search), hash: location.hash },
      { replace: true },
    );
    if (getTutorialById(id)) startTutorial(id, { from: "academy" });
  }, [user, location.search, location.pathname, location.hash, navigate, startTutorial]);

  const replayTutorial = useCallback(
    async (id: string, options?: StartTutorialOptions) => {
      try {
        await axios.delete(`${seenEndpoint}/${id}`);
        setSeen((prev) => prev.filter((x) => x !== id));
        startTutorial(id, options);
      } catch (err) {
        console.error("Failed to reset tutorial:", err);
      }
    },
    [startTutorial, seenEndpoint],
  );

  const dismissCurrent = useCallback(() => {
    endTour(true);
  }, [endTour]);

  // joyride reads `spotlightClicks` per step; `advanceOn` steps also let
  // clicks pass through the dimmed overlay (see TourStep.advanceOn).
  const joyrideSteps = useMemo<Step[]>(
    () =>
      (runningTour?.steps ?? []).map((s) => {
        const step: TourStep = {
          ...s,
          spotlightClicks: s.spotlightClicks ?? !!s.advanceOn,
        };
        if (s.advanceOn) {
          step.styles = {
            ...s.styles,
            overlay: { ...(s.styles?.overlay ?? {}), pointerEvents: "none" },
          };
        }
        return step as Step;
      }),
    [runningTour],
  );

  const value = useMemo<TutorialContextValue>(
    () => ({
      pendingTutorials,
      allTutorials: ALL_TUTORIALS,
      seen,
      isRunning: !!runningId,
      runningTourId: runningId,
      runningFrom,
      startTutorial,
      replayTutorial,
      dismissCurrent,
    }),
    [
      pendingTutorials,
      seen,
      runningId,
      runningFrom,
      startTutorial,
      replayTutorial,
      dismissCurrent,
    ],
  );

  return (
    <TutorialContext.Provider value={value}>
      {children}
      {runningTour && (
        <Joyride
          steps={joyrideSteps}
          stepIndex={stepIndex}
          run
          continuous
          showSkipButton
          disableScrollParentFix
          disableOverlayClose
          disableCloseOnEsc
          spotlightClicks={false}
          callback={handleJoyrideCallback}
          tooltipComponent={TutorialTooltip}
          styles={{
            options: {
              zIndex: 10000,
              // Matches the tooltip's bg-base-100 in each theme.
              arrowColor: resolvedTheme === "dark" ? "#1d232a" : "#ffffff",
              overlayColor: "rgba(15, 23, 42, 0.5)",
            },
          }}
        />
      )}
    </TutorialContext.Provider>
  );
};
